import React, { useMemo, useState } from 'react'

import moment from 'moment'
import * as R from 'ramda'
import { useSelector } from 'react-redux'

import {
  Button,
  Flex,
  Header,
  InlineInput,
  Modal,
  Select,
  Text,
  useToast,
} from '@driftt/dds'

import { teams } from 'modules/teams/selectors'

import { SalesRepRows } from '../types'
import { useSalesSetupData } from '../useSalesRepSetup.hook'

import ModalTable from './ModalTable'
interface SalesRepModalProps {
  isOpen: boolean
  onClose: () => void
  salesRepRow: SalesRepRows
}
const SalesRepModal = ({
  isOpen,
  onClose,
  salesRepRow,
}: SalesRepModalProps) => {
  const triggerToast = useToast()
  const allTeams = useSelector(teams)
  const userTeams = useMemo(
    () =>
      salesRepRow.teamIds.map((teamId) => {
        const team = allTeams.find((team) => team.id === teamId)
        return {
          label: team?.name,
          value: team?.id,
        }
      }),
    [allTeams, salesRepRow]
  )
  const { saveSalesRepData, isSavingName, isSavingTeam } = useSalesSetupData({})
  const [selectedTeams, setSelectedTeams] = useState<any[]>(userTeams)
  const [name, setName] = useState<string>(salesRepRow.name || '')

  const teamOptions = useMemo(
    () =>
      allTeams.map(({ name, id }) => ({
        label: name,
        value: id,
      })),
    [allTeams]
  )
  const selectedTeamIds = selectedTeams.map((team) => team.value)
  const userTeamIds = userTeams.map((team) => team.value)
  const nameChanged = name !== salesRepRow.name
  const teamChanged = !(
    userTeamIds.length === selectedTeamIds.length &&
    userTeamIds.every((userTeam) => selectedTeamIds.includes(userTeam))
  )
  const isSaving = isSavingName || isSavingTeam

  const formatLastActive = (lastActive?: number) => {
    const lastActiveMoment = moment(lastActive)
    const momentOneYearAgo = moment().subtract(1, 'year')
    return lastActiveMoment.isBefore(momentOneYearAgo)
      ? 'Over a year ago'
      : lastActiveMoment.format('h:mm A [on] MMMM D, YYYY')
  }

  const handleSave = () => {
    const addToTeamIds = selectedTeamIds.filter(
      (item) => !userTeamIds?.includes(item)
    )
    const removeFromTeamIds = userTeamIds?.filter(
      (item) => !selectedTeamIds.includes(item)
    )
    const userData = {
      userId: salesRepRow.id,
      name: name,
    }
    saveSalesRepData({
      teamChanged,
      nameChanged,
      teamData: {
        userId: salesRepRow.id,
        addToTeamIds,
        removeFromTeamIds,
      },
      userData,
      onSuccess: () => {
        triggerToast({ variant: 'success', message: 'Saved!' })
        onClose()
      },
      onError: (error) => {
        console.error('Failed to update sales rep', error)
      },
    })
  }

  const title = (
    <InlineInput
      label="Name"
      value={name || 'Enter full name'}
      onChange={(e) => setName(e.target.value)}
    />
  ) as unknown as string

  return (
    <Modal.Root open={isOpen} onClose={onClose}>
      <Modal.Dialog size="medium" variant="info">
        <Modal.Title title={title}>
          <Text isPrivate css={{ paddingTop: 12 }}>
            {salesRepRow?.email}
          </Text>
        </Modal.Title>
        <Modal.Content
          as={Flex}
          direction="column"
          gap={2}
          scrollOverflow="outside"
        >
          <Flex direction="column">
            <Header variant="h4">Last Active</Header>
            <Text>{formatLastActive(salesRepRow?.lastActive)}</Text>
          </Flex>
          <Flex direction="column">
            <Header variant="h4">Roles</Header>
            <Text>{R.pathOr([], ['roleNames'], salesRepRow).join(', ')}</Text>
          </Flex>
          <Flex direction="column">
            <Header variant="h4">Teams</Header>
            <Text>
              <Flex>
                <Select
                  label="Select team..."
                  maxMenuHeight={140}
                  hideLabel
                  placeholder="Select team..."
                  multi
                  value={selectedTeams}
                  options={teamOptions}
                  onChange={(teams: any) => setSelectedTeams(teams)}
                />
              </Flex>
            </Text>
          </Flex>
          <ModalTable tableData={salesRepRow} />
        </Modal.Content>
        <Modal.Actions>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!nameChanged && !teamChanged}
            variant="primary"
            onClick={handleSave}
            loading={isSaving}
          >
            Save
          </Button>
        </Modal.Actions>
      </Modal.Dialog>
    </Modal.Root>
  )
}

export default SalesRepModal
